// =============================================================================
// Profile Page - Account personale (nome, email, password)
// =============================================================================
// Area account dell'utente autenticato, distinta da Settings: qui si modifica
// la persona, non l'organizzazione. Per questo è accessibile a tutti i ruoli.
// =============================================================================

import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, KeyRound, Save, ShieldCheck, UserCircle } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useTenantStore } from '../store/tenantStore';
import { useChangePassword, useUpdateProfile } from '../api/hooks';
import { useTranslatedError } from '../hooks/useTranslatedError';
import { LanguageSelector } from '../components/common/LanguageSelector';
import {
  Button,
  Feedback,
  FeedbackProps,
  FormField,
  SectionHeader,
  SettingsCard,
  TextInput,
} from '../components/settings/_shared';

const MIN_PASSWORD_LENGTH = 6;

// -----------------------------------------------------------------------------
// Input password con toggle di visibilità
// -----------------------------------------------------------------------------

function PasswordInput({
  value,
  onChange,
  autoComplete,
  disabled,
  placeholder,
  revealLabel,
  hideLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  disabled?: boolean;
  placeholder?: string;
  revealLabel: string;
  hideLabel: string;
}) {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;

  return (
    <div className="relative">
      <TextInput
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        disabled={disabled}
        placeholder={placeholder}
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        title={visible ? hideLabel : revealLabel}
        aria-label={visible ? hideLabel : revealLabel}
        className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-400 hover:text-gray-600"
      >
        <Icon className="w-4 h-4" />
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Card 1 — Informazioni personali
// -----------------------------------------------------------------------------

function PersonalInfoCard() {
  const { t } = useTranslation('auth');
  const { getErrorMessage } = useTranslatedError();
  const user = useAuthStore((s) => s.user);
  const updateProfile = useUpdateProfile();

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [feedback, setFeedback] = useState<FeedbackProps | null>(null);

  // Il profilo può cambiare anche altrove (un altro tab, un salvataggio
  // precedente): riallinea i campi non appena lo store si aggiorna.
  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
    setEmail(user?.email ?? '');
  }, [user?.firstName, user?.lastName, user?.email]);

  const emailChanged = useMemo(
    () => email.trim().toLowerCase() !== (user?.email ?? '').toLowerCase(),
    [email, user?.email],
  );

  const isDirty =
    emailChanged ||
    firstName.trim() !== (user?.firstName ?? '') ||
    lastName.trim() !== (user?.lastName ?? '');

  const canSave =
    isDirty &&
    !!firstName.trim() &&
    !!lastName.trim() &&
    !!email.trim() &&
    (!emailChanged || !!currentPassword);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setFeedback(null);

    try {
      await updateProfile.mutateAsync({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        // Inviata solo quando serve: cambiare nome non richiede conferma
        ...(emailChanged ? { currentPassword } : {}),
      });
      setCurrentPassword('');
      setFeedback({ type: 'success', message: t('profile.saved') });
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <SettingsCard>
      <SectionHeader
        icon={UserCircle}
        title={t('profile.personal.title')}
        description={t('profile.personal.description')}
      />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label={t('register.firstNameLabel')} required>
            <TextInput
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('register.firstNamePlaceholder')}
              disabled={updateProfile.isPending}
              maxLength={100}
            />
          </FormField>

          <FormField label={t('register.lastNameLabel')} required>
            <TextInput
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t('register.lastNamePlaceholder')}
              disabled={updateProfile.isPending}
              maxLength={100}
            />
          </FormField>
        </div>

        <FormField label={t('emailLabel')} hint={t('profile.personal.emailHint')} required>
          <TextInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('emailPlaceholder')}
            disabled={updateProfile.isPending}
          />
        </FormField>

        {/* La password compare solo quando serve: l'email è anche la credenziale
            di accesso, cambiarla richiede una conferma di identità. */}
        {emailChanged && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-2 mb-3">
              <ShieldCheck className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{t('profile.personal.emailConfirm')}</p>
            </div>
            <FormField label={t('profile.currentPasswordLabel')} required>
              <PasswordInput
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
                disabled={updateProfile.isPending}
                placeholder={t('passwordPlaceholder')}
                revealLabel={t('profile.showPassword')}
                hideLabel={t('profile.hidePassword')}
              />
            </FormField>
          </div>
        )}

        <div className="flex items-center gap-4 pt-2">
          <Button type="submit" icon={Save} loading={updateProfile.isPending} disabled={!canSave}>
            {t('profile.save')}
          </Button>
          {feedback && <Feedback {...feedback} />}
        </div>
      </form>
    </SettingsCard>
  );
}

// -----------------------------------------------------------------------------
// Card 2 — Password
// -----------------------------------------------------------------------------

function PasswordCard() {
  const { t } = useTranslation('auth');
  const { getErrorMessage } = useTranslatedError();
  const changePassword = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [feedback, setFeedback] = useState<FeedbackProps | null>(null);

  const tooShort = newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const canSave =
    !!currentPassword &&
    newPassword.length >= MIN_PASSWORD_LENGTH &&
    newPassword === confirmPassword;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSave) return;
    setFeedback(null);

    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setFeedback({ type: 'success', message: t('profile.password.changed') });
    } catch (error) {
      setFeedback({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <SettingsCard>
      <SectionHeader
        icon={KeyRound}
        title={t('profile.password.title')}
        description={t('profile.password.description')}
      />

      <form onSubmit={handleSubmit} className="space-y-4 max-w-md">
        <FormField label={t('profile.currentPasswordLabel')} required>
          <PasswordInput
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
            disabled={changePassword.isPending}
            placeholder={t('passwordPlaceholder')}
            revealLabel={t('profile.showPassword')}
            hideLabel={t('profile.hidePassword')}
          />
        </FormField>

        <FormField
          label={t('profile.password.newLabel')}
          hint={t('register.passwordHint')}
          required
        >
          <PasswordInput
            value={newPassword}
            onChange={setNewPassword}
            autoComplete="new-password"
            disabled={changePassword.isPending}
            placeholder={t('passwordPlaceholder')}
            revealLabel={t('profile.showPassword')}
            hideLabel={t('profile.hidePassword')}
          />
        </FormField>

        <FormField label={t('register.confirmPasswordLabel')} required>
          <PasswordInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            disabled={changePassword.isPending}
            placeholder={t('passwordPlaceholder')}
            revealLabel={t('profile.showPassword')}
            hideLabel={t('profile.hidePassword')}
          />
        </FormField>

        {tooShort && <Feedback type="error" message={t('register.passwordTooShort')} />}
        {mismatch && <Feedback type="error" message={t('register.passwordMismatch')} />}

        <div className="flex items-center gap-4 pt-2">
          <Button type="submit" icon={Save} loading={changePassword.isPending} disabled={!canSave}>
            {t('profile.password.submit')}
          </Button>
          {feedback && <Feedback {...feedback} />}
        </div>
      </form>
    </SettingsCard>
  );
}

// -----------------------------------------------------------------------------
// Pagina
// -----------------------------------------------------------------------------

export interface ProfilePageProps {
  /**
   * Reso fuori dalla AppShell (nessuna organizzazione attiva): senza sidebar
   * servono qui il ritorno indietro e il selettore di lingua.
   */
  standalone?: boolean;
}

export function ProfilePage({ standalone = false }: ProfilePageProps) {
  const { t } = useTranslation('auth');
  const user = useAuthStore((s) => s.user);
  const activeRole = useTenantStore((s) => s.activeRole);
  const memberships = useTenantStore((s) => s.memberships);

  // Da dove si e' arrivati quando manca un'organizzazione attiva: chi non ne ha
  // nessuna sta facendo onboarding, gli altri stanno scegliendo.
  const backTo = memberships.length > 0 ? '/select-tenant' : '/onboarding';

  return (
    <div className={`flex flex-col bg-gray-50 ${standalone ? 'min-h-screen' : 'h-full'}`}>
      <header className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {standalone ? (
              <Link
                to={backTo}
                title={t('profile.back')}
                aria-label={t('profile.back')}
                className="p-2 -ml-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
            ) : (
              <div className="p-2 bg-emerald-50 rounded-lg flex-shrink-0">
                <UserCircle className="w-5 h-5 text-emerald-600" />
              </div>
            )}
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 leading-tight">
                {t('profile.title')}
              </h1>
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {user?.email}
                {activeRole && !standalone ? ` · ${activeRole}` : ''}
              </p>
            </div>
          </div>
          {standalone && <LanguageSelector />}
        </div>
      </header>

      <main className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
          <PersonalInfoCard />
          <PasswordCard />
        </div>
      </main>
    </div>
  );
}

export default ProfilePage;
